import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { consoleLocationService } from '@/services/consoleLocationService';
import { isCustomLocationId, parseGeoCode } from '@/types/consoleLocation';

interface LocationDetailModalProps {
  isOpen: boolean;
  lgdCode: string | null;
  onClose: () => void;
}

/**
 * Format a lat/lng value for display. The backend can send it as a
 * number, a stringified number, or null; anything else falls back
 * to `—`. Six decimals is ~11 cm resolution — enough for village
 * boundaries, small enough to keep the table cell narrow.
 */
const formatCoord = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(6);
};

const KNOWN_FIELDS = new Set([
  'id',
  'location_name_in_english',
  'location_alias_name',
  'location_name_in_local',
  'subdistrict_name_in_english',
  'district_name_in_english',
  'state_name_in_english',
  'block_name_in_english',
  'pincode',
  'location_code',
  'village_code',
  // Coordinates are surfaced via the parsed geo_code fields below,
  // so all three names are hidden from the "Additional fields"
  // dump — otherwise the modal would show geo_code as raw text
  // alongside the parsed lat/lng.
  'geo_code',
  'latitude',
  'longitude',
  'altitude',
  'created_at',
  'updated_at',
]);

export const LocationDetailModal = ({ isOpen, lgdCode, onClose }: LocationDetailModalProps) => {
  useEffect(() => {
    if (!isOpen) return;
    // Reflect open state on the body so map/pan behind the modal
    // doesn't scroll. Cheap side-effect — reverted on close/unmount.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const query = useQuery({
    queryKey: ['console-location', 'detail', lgdCode],
    queryFn: () => consoleLocationService.getById(lgdCode as string),
    enabled: Boolean(isOpen && lgdCode),
    staleTime: 5 * 60 * 1000,
  });

  if (!isOpen) return null;

  const data = query.data;
  // Anything the backend sent that isn't in our documented field
  // list — surfaced under "Additional fields" so a schema drift
  // doesn't hide information from an admin trying to debug a row.
  const extras = data
    ? Object.entries(data as Record<string, unknown>).filter(
        ([key, value]) =>
          !KNOWN_FIELDS.has(key) &&
          value !== null &&
          value !== undefined &&
          !(typeof value === 'string' && value === ''),
      )
    : [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="location-detail-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-rgf-gray-200">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 p-2 rounded-lg bg-rgf-primary-100">
              <MapPin className="h-5 w-5 text-rgf-primary-600" />
            </div>
            <div>
              <h3 id="location-detail-title" className="text-lg font-semibold text-rgf-gray-900">
                Location details
              </h3>
              <p className="text-xs font-mono text-rgf-gray-500 mt-0.5">
                {lgdCode ?? '—'}
                {lgdCode && isCustomLocationId(lgdCode) && (
                  <span className="ml-2 badge badge-warning">Custom</span>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-rgf-gray-100 text-rgf-gray-500"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          {query.isLoading ? (
            <div className="py-8 text-center text-rgf-gray-500">Loading location…</div>
          ) : query.error ? (
            <div className="py-8 text-center space-y-3">
              <p className="text-sm text-rgf-gray-700">
                {(query.error as Error)?.message || 'Failed to load location details.'}
              </p>
              <Button size="sm" onClick={() => query.refetch()}>
                Try Again
              </Button>
            </div>
          ) : data ? (
            <div className="space-y-6">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                <Field label="Name (English)" value={data.location_name_in_english} />
                <Field label="Alias" value={data.location_alias_name} />
                <Field label="Name (Local)" value={data.location_name_in_local} />
                <Field label="District" value={data.district_name_in_english} />
                <Field label="Subdistrict" value={data.subdistrict_name_in_english} />
                <Field label="Block" value={(data.block_name_in_english as string | null) ?? null} />
                <Field label="State" value={data.state_name_in_english} />
                <Field label="Pincode" value={data.pincode?.toString() ?? null} />
                <Field
                  label="Location code (LGD)"
                  value={data.location_code === 0 ? 'Custom (0)' : data.location_code?.toString()}
                />
                {(() => {
                  // Coords live in `geo_code` ("lat;lng"); explicit
                  // latitude/longitude fields are a forward-compat
                  // fallback in case a future backend adds them.
                  const parsed = parseGeoCode(data.geo_code);
                  const lat = parsed.latitude ?? data.latitude;
                  const lng = parsed.longitude ?? data.longitude;
                  return (
                    <>
                      <Field label="Latitude" value={formatCoord(lat)} mono />
                      <Field label="Longitude" value={formatCoord(lng)} mono />
                    </>
                  );
                })()}
                {(data.altitude ?? null) !== null && (
                  <Field label="Altitude" value={String(data.altitude)} mono />
                )}
              </dl>

              {extras.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-rgf-gray-500 mb-2">
                    Additional fields
                  </h4>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                    {extras.map(([key, value]) => (
                      <Field key={key} label={key} value={String(value)} mono />
                    ))}
                  </dl>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex justify-end px-6 py-3 bg-rgf-gray-50 border-t border-rgf-gray-200">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

const Field = ({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) => (
  <div>
    <dt className="text-xs font-medium text-rgf-gray-500 uppercase tracking-wide">{label}</dt>
    <dd
      className={`mt-0.5 text-sm text-rgf-gray-900 ${mono ? 'font-mono' : ''} ${
        !value || value === '—' ? 'text-rgf-gray-400' : ''
      }`}
    >
      {value && value !== '' ? value : '—'}
    </dd>
  </div>
);
